import React from 'react';
import { FiExternalLink, FiFileText, FiImage, FiVideo } from 'react-icons/fi';
import type { DossierDocument, DossierDocumentsSection, DossierOpportunityRef } from '../../../services/CrmDossierService';
import DossierSectionState from './DossierSectionState';
import DossierMoveMenu from './DossierMoveMenu';
import { DIRECTION_LABELS, DOCUMENT_SOURCE_LABELS, formatDossierWhen } from './dossierFormat';

interface Props {
  section: DossierDocumentsSection;
  contactName: string;
  busyItemId?: string | null;
  onLink: (itemId: string) => void;
  onUnlink: (itemId: string) => void;
  /** Outras oportunidades do mesmo cliente: destino possível da correção de atribuição. */
  otherOpportunities?: DossierOpportunityRef[];
  onMove?: (itemId: string, targetCardId: number) => void;
  onOpen: (path: string) => void;
  onRetry?: () => void;
}

const NOT_STORED: Record<string, string> = {
  proposal: 'Cotação enviada pela IA. O arquivo foi para o WhatsApp e não ficou guardado aqui.',
  registrar: 'O Registrar leu o arquivo; o arquivo em si não foi guardado.',
};

function openLabel(source: DossierDocument['source']): string {
  if (source === 'registrar') return 'Ver no Registrar';
  if (source === 'email') return 'Abrir e-mail';
  return 'Ver na conversa';
}

function KindIcon({ kind }: { kind: string }) {
  if (kind === 'image') return <FiImage aria-hidden />;
  if (kind === 'video') return <FiVideo aria-hidden />;
  return <FiFileText aria-hidden />;
}

const DossierDocuments: React.FC<Props> = ({ section, contactName, busyItemId, onLink, onUnlink, otherOpportunities = [], onMove, onOpen, onRetry }) => {
  const own = section.items.filter((i) => i.scope === 'card');
  const fromContact = section.items.filter((i) => i.scope === 'contact');

  const renderItem = (d: DossierDocument) => (
    <li key={d.id} className="dossier-item" data-testid={`dossier-document-${d.id}`}>
      <div className="dossier-item__icon"><KindIcon kind={d.fileKind} /></div>
      <div className="dossier-item__body">
        <strong className="dossier-item__title">{d.fileName}</strong>
        {d.scope === 'card' && !d.linked && otherOpportunities.length > 0 && (
          <span className="dossier-chip" title="Veio da própria oportunidade principal; mova se for de outra.">atribuído automaticamente</span>
        )}
        <span className="dossier-item__meta">
          {[DOCUMENT_SOURCE_LABELS[d.source] ?? d.source, DIRECTION_LABELS[d.direction] ?? d.direction, formatDossierWhen(d.at)]
            .filter(Boolean)
            .join(' · ')}
        </span>
        {d.summary && <span className="dossier-item__summary">{d.summary}</span>}
        {d.stored && d.url ? (
          <a className="dossier-link" href={d.url} target="_blank" rel="noopener noreferrer">
            <FiExternalLink aria-hidden /> Abrir documento
          </a>
        ) : (
          <span className="dossier-item__note">{NOT_STORED[d.source] ?? 'Arquivo não guardado na plataforma.'}</span>
        )}
        <div className="dossier-item__actions">
          <button type="button" className="dossier-btn" onClick={() => onOpen(d.openPath)}>
            {openLabel(d.source)}
          </button>
          {d.scope === 'contact' && (
            <button type="button" className="dossier-btn dossier-btn--primary" disabled={busyItemId === d.id} onClick={() => onLink(d.id)}>
              Vincular a esta oportunidade
            </button>
          )}
          {d.scope === 'card' && onMove && (
            <DossierMoveMenu itemId={d.id} others={otherOpportunities} disabled={busyItemId === d.id} onMove={onMove} />
          )}
          {d.scope === 'card' && d.linked && (
            <button type="button" className="dossier-btn" disabled={busyItemId === d.id} onClick={() => onUnlink(d.id)}>
              Desvincular
            </button>
          )}
        </div>
      </div>
    </li>
  );

  return (
    <section className="dossier-card" data-testid="dossier-documents">
      <h3 className="dossier-card__title">Documentos</h3>
      <DossierSectionState
        status={section.status}
        isEmpty={section.items.length === 0}
        emptyText="Nenhum documento nesta oportunidade."
        unavailableText="Não deu para carregar os documentos agora."
        failedSources={section.failedSources}
        onRetry={onRetry}
      >
        {own.length > 0 && <ul className="dossier-list">{own.map(renderItem)}</ul>}
        {fromContact.length > 0 && (
          <div className="dossier-group" data-testid="dossier-documents-contact">
            <h4 className="dossier-group__title">Do cliente, sem oportunidade definida</h4>
            <p className="dossier-note">{`Valem para todas as oportunidades de ${contactName} até você vincular a uma delas.`}</p>
            <ul className="dossier-list">{fromContact.map(renderItem)}</ul>
          </div>
        )}
      </DossierSectionState>
      {section.status !== 'unavailable' && section.otherOpportunityCount > 0 && (
        <p className="dossier-note">{`${section.otherOpportunityCount} documento(s) em outras oportunidades deste cliente.`}</p>
      )}
      {section.status !== 'unavailable' && section.truncated && <p className="dossier-note">Mostrando os mais recentes.</p>}
    </section>
  );
};

export default DossierDocuments;
